import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

const BLOG_URL = '/data/blog.json';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  featured_image?: string;
  tags?: string[];
  published_at: string;
}

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const markdownStyles: React.CSSProperties = {};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    fetch(BLOG_URL)
      .then(r => r.json())
      .then((posts: BlogPost[]) => {
        const found = posts.find(p => p.slug === slug);
        if (found) setPost(found);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-28 pb-16 text-center">
        <div className="inline-block w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="pt-28 pb-16 px-4 max-w-3xl mx-auto text-center">
        <h1 className="text-3xl font-bold text-slate-800 mb-4">Post Not Found</h1>
        <p className="text-gray-500 mb-6">The blog post you're looking for doesn't exist.</p>
        <button onClick={() => navigate('/blog')} className="text-green-600 font-semibold hover:underline">
          Back to Blog
        </button>
      </div>
    );
  }

  const tags = Array.isArray(post.tags) ? post.tags : (post.tags ? String(post.tags).split(',').map(t => t.trim()) : []);

  return (
    <div className="pt-24 pb-16">
      {post.featured_image && (
        <div className="max-w-5xl mx-auto px-4 mb-8">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-64 sm:h-80 object-cover rounded-xl shadow-md"
            onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
          />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-4">
        <button onClick={() => navigate('/blog')} className="text-green-600 font-medium hover:underline mb-6 block">
          &larr; Back to Blog
        </button>

        <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-3 leading-tight">
          {post.title}
        </h1>

        <div className="flex items-center gap-2 text-sm text-gray-400 mb-8">
          <span>By {post.author}</span>
          <span>·</span>
          <span>{formatDate(post.published_at)}</span>
        </div>

        <div className="blog-content">
          <ReactMarkdown
            components={{
              h1: ({children}) => <h1 className="text-3xl font-bold text-slate-800 mt-8 mb-3">{children}</h1>,
              h2: ({children}) => <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-3">{children}</h2>,
              h3: ({children}) => <h3 className="text-xl font-semibold text-slate-800 mt-6 mb-2">{children}</h3>,
              h4: ({children}) => <h4 className="text-lg font-semibold text-slate-800 mt-4 mb-2">{children}</h4>,
              p: ({children}) => <p className="text-gray-700 leading-relaxed mb-4">{children}</p>,
              ul: ({children}) => <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700">{children}</ul>,
              ol: ({children}) => <ol className="list-decimal pl-6 mb-4 space-y-1 text-gray-700">{children}</ol>,
              li: ({children}) => <li className="leading-relaxed">{children}</li>,
              a: ({href, children}) => <a href={href} className="text-green-600 underline hover:text-green-700" target="_blank" rel="noopener noreferrer">{children}</a>,
              blockquote: ({children}) => <blockquote className="border-l-4 border-green-500 pl-4 my-4 text-gray-500 italic">{children}</blockquote>,
              code: ({className, children, ...props}) => {
                const isBlock = className?.includes('language-');
                return isBlock
                  ? <pre className="bg-slate-800 text-slate-200 p-4 rounded-lg overflow-x-auto my-4"><code>{children}</code></pre>
                  : <code className="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-sm">{children}</code>;
              },
              pre: ({children}) => <>{children}</>,
              img: ({src, alt}) => <img src={src} alt={alt || ''} className="rounded-lg shadow-md my-6 max-w-full" />,
              hr: () => <hr className="border-t border-gray-200 my-8" />,
              strong: ({children}) => <strong className="font-bold text-slate-800">{children}</strong>,
              em: ({children}) => <em className="italic">{children}</em>,
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        {tags.length > 0 && (
          <div className="mt-10 pt-6 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, i) => (
                <span key={i} className="px-3 py-1 text-sm font-medium text-green-700 bg-green-50 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 p-6 bg-slate-800 rounded-xl text-center">
          <h3 className="text-xl font-bold text-white mb-2">Need Roofing Help?</h3>
          <p className="text-gray-300 mb-4">Contact Lemay Roofing for a free estimate on your project.</p>
          <button
            onClick={() => navigate('/request-quote')}
            className="bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Get a Free Quote
          </button>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
